import React, { useEffect, useState } from "react";
import { AlarmRow } from "@src/cloud-connect/alarms/entities/alarm-row";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useDispatch } from "react-redux";
import checkBoxIcon from "@src/assets/svg/checkbox-icon.svg";
import checkBoxCheckedIcon from "@src/assets/svg/checked-checkbox-icon.svg";
import nodeIconGray from "@src/assets/svg/node-icon-gray.svg";
import nodeIconWhite from "@src/assets/svg/node-icon-white.svg";
import {
  updateAlarmNodeSelectionData,
  updateAlarmNodeSelectionState,
} from "@src/reducers/your-reducer";
import { OurNode } from "@src/cloud-connect/poc/our-node";
import { CreateAncestorsLineageForSidePanel } from "@src/cloud-connect/node-manager/entities/create-ancestors-lineage-for-side-panel";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { handleConditionOnBlur } from "./handle-condition-on-blur";
import { generateMinDates } from "./generate-min-dates";
import { generateHourDates } from "./generate-hour-dates";
import { translateCondition } from "./translate-condition";
import { formatPathFromSegments } from "@src/cloud-connect/utils/format-path-from-segments";
import { useI18N } from "@src/language/language-context";

export const useAlarmListNodeHook = (
  node: OurNode,
  alarmRow: AlarmRow,
  firstNumericInputRef: React.RefObject<HTMLInputElement>,
  secondNumericInputRef: React.RefObject<HTMLInputElement>,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [isCheckBoxChecked, setIsCheckBoxChecked] = useState(
    alarmRow.getSelected(),
  );
  const [isAlarmActive, setIsAlarmActive] = useState(alarmRow.getSelected());

  const [firstCondition, setFirstCondition] = useState<string>("");
  const [firstOperator, setFirstOperator] = useState("<");

  const [secondCondition, setSecondCondition] = useState<string>("");
  const [secondOperator, setSecondOperator] = useState(">");

  const texts = {
    emptyInputWarning: i18n("modules.alarmListNode.emptyInputWarning"),
    enterValidNumber: i18n("modules.alarmListNode.enterValidNumber"),
    firstConditionMustBeSmaller: i18n(
      "modules.alarmListNode.firstConditionMustBeSmaller",
    ),
    secondConditionMustBeGreater: i18n(
      "modules.alarmListNode.secondConditionMustBeGreater",
    ),
  };

  const toggleHandleClick = () => {
    alarmRow.getAlarm().setActive(!isAlarmActive);
    if (alarmRow.getAlarm().getDataType() === "numeric") {
      if (
        alarmRow.getAlarm().getActive() &&
        firstCondition === "" &&
        secondCondition === ""
      ) {
        triggerEmptyInputWarning();
        return;
      }
    } else {
      if (alarmRow.getAlarm().getActive() && firstCondition === "") {
        triggerEmptyInputWarning();
        return;
      }
    }
  };

  const returnCheckboxIconByState = () => {
    return isCheckBoxChecked ? checkBoxCheckedIcon : checkBoxIcon;
  };

  const returnIcon = (alarmState) => {
    return alarmState ? nodeIconWhite : nodeIconGray;
  };

  const returnUnit = (node) => {
    let unit;

    if (
      node &&
      node.attributes &&
      node.attributes.unit &&
      node.attributes.unit.display_name
    ) {
      unit = node.attributes.unit.display_name;
    } else if (
      alarmRow.getAlarm().getOurNode().attributes &&
      alarmRow.getAlarm().getOurNode().attributes.unit &&
      alarmRow.getAlarm().getOurNode().attributes.unit.display_name
    ) {
      unit = alarmRow.getAlarm().getOurNode().attributes.unit.display_name;
    } else {
      unit = "";
    }

    return unit;
  };

  const handleCheckBoxClick = () => {
    alarmRow.setSelected(!alarmRow.getSelected());
  };

  const handleClickOnNode = () => {
    dispatch(
      updateAlarmNodeSelectionData({
        nodeId: node.id,
        name: node.name,
        unit: returnUnit(node),
        dataType: alarmRow.getAlarm().getDataType(),
      }),
    );
    dispatch(updateAlarmNodeSelectionState("show"));
  };

  const buildAncestorBreadCrumbs = () => {
    return formatPathFromSegments(
      new CreateAncestorsLineageForSidePanel().createMachineStructureAncestorsLineageForSidePanel(
        node.id,
      ),
      { trimStart: false, trimEnd: false },
    );
  };

  const renderCheckBoxButton = () => {
    return (
      <IconButton
        icon={returnCheckboxIconByState()}
        fun={handleCheckBoxClick}
      />
    );
  };

  const handleFirstMatchOnClick = (operatorInputValue) => {
    setFirstOperator(operatorInputValue);
    handleConditionOnBlur(
      alarmRow,
      firstCondition,
      secondCondition,
      operatorInputValue,
      secondOperator,
    );
  };

  const handleSecondMatchOnClick = (operatorInputValue) => {
    setSecondOperator(operatorInputValue);
    handleConditionOnBlur(
      alarmRow,
      firstCondition,
      secondCondition,
      firstOperator,
      operatorInputValue,
    );
  };

  const triggerEmptyInputWarning = () => {
    warningSnackbar(texts.emptyInputWarning, 5000);
  };

  const handleFirstConditionOnBlur = () => {
    if (
      alarmRow.getAlarm().getActive() &&
      firstCondition === "" &&
      secondCondition === ""
    ) {
      triggerEmptyInputWarning();
      return;
    }

    if (alarmRow.getAlarm().getDataType() === "numeric") {
      const validNumber = firstCondition;

      if (
        validNumber !== "" &&
        secondCondition !== "" &&
        parseFloat(String(validNumber)) > parseFloat(String(secondCondition))
      ) {
        setFirstCondition("");
        warningSnackbar(texts.firstConditionMustBeSmaller, 5000);

        // using setTimeout to avoid conflicts in the browser's event loop
        setTimeout(() => {
          if (firstNumericInputRef?.current) {
            firstNumericInputRef.current.focus();
          }
        }, 0);
        return;
      }

      setFirstCondition(validNumber);
      handleConditionOnBlur(
        alarmRow,
        validNumber,
        secondCondition,
        firstOperator,
        secondOperator,
      );
    } else {
      handleConditionOnBlur(
        alarmRow,
        firstCondition,
        secondCondition,
        firstOperator === "<" ? "=" : firstOperator,
        secondOperator,
      );
    }
  };

  const handleSecondConditionOnBlur = () => {
    if (
      alarmRow.getAlarm().getActive() &&
      firstCondition === "" &&
      secondCondition === ""
    ) {
      triggerEmptyInputWarning();
      return;
    }

    if (alarmRow.getAlarm().getDataType() === "numeric") {
      const validNumber = secondCondition;

      if (
        validNumber !== "" &&
        firstCondition !== "" &&
        parseFloat(String(validNumber)) < parseFloat(String(firstCondition))
      ) {
        setSecondCondition("");
        warningSnackbar(texts.secondConditionMustBeGreater, 5000);

        // using setTimeout to avoid conflicts in the browser's event loop
        setTimeout(() => {
          if (secondNumericInputRef?.current) {
            secondNumericInputRef.current.focus();
          }
        }, 0);
        return;
      }

      setSecondCondition(validNumber);
      handleConditionOnBlur(
        alarmRow,
        firstCondition,
        validNumber,
        firstOperator,
        secondOperator,
      );
    } else {
      handleConditionOnBlur(
        alarmRow,
        firstCondition,
        secondCondition,
        firstOperator,
        secondOperator,
      );
    }
  };

  useEffect(() => {
    const alarm = alarmRow.getAlarm();
    const conditions = alarm.getCriteria().getConditions();

    if (alarmRow.getAlarm()) {
      setIsAlarmActive(alarm.getActive());

      conditions.forEach((condition) => {
        if (
          condition.getOperator() === "less-than" ||
          condition.getOperator() === "less-than-or-equal"
        ) {
          setFirstOperator(translateCondition(condition.getOperator()));
          setFirstCondition(condition.getUserInput().toString());
        } else if (
          condition.getOperator() === "greater-than" ||
          condition.getOperator() === "greater-than-or-equal"
        ) {
          setSecondOperator(translateCondition(condition.getOperator()));
          setSecondCondition(condition.getUserInput().toString());
        } else {
          setFirstOperator(translateCondition(condition.getOperator()));
          setFirstCondition(condition.getUserInput().toString());
        }
      });
    }
  }, []);

  useEffect(() => {
    const updateCheckBoxState = () => {
      setIsCheckBoxChecked(alarmRow.getSelected());
    };

    const alarm = alarmRow.getAlarm();
    const conditions = alarm.getCriteria().getConditions();

    if (alarmRow.getAlarm()) {
      setIsAlarmActive(alarm.getActive());

      conditions.forEach((condition) => {
        if (
          condition.getOperator() === "less-than" ||
          condition.getOperator() === "less-than-or-equal"
        ) {
          setFirstOperator(translateCondition(condition.getOperator()));
          setFirstCondition(condition.getUserInput().toString());
        } else if (
          condition.getOperator() === "greater-than" ||
          condition.getOperator() === "greater-than-or-equal"
        ) {
          setSecondOperator(translateCondition(condition.getOperator()));
          setSecondCondition(condition.getUserInput().toString());
        } else {
          setFirstOperator(translateCondition(condition.getOperator()));
          setFirstCondition(condition.getUserInput().toString());
        }
      });
    }

    alarmRow.addObserver(updateCheckBoxState);

    updateCheckBoxState();

    return () => {
      alarmRow.removeObserver(updateCheckBoxState);
    };
  }, [alarmRow]);

  useEffect(() => {
    const alarm = alarmRow.getAlarm();
    const updateToggleState = () => {
      setIsAlarmActive(alarm.getActive());
    };

    alarm.addObserver(updateToggleState);

    updateToggleState();

    return () => {
      alarm.removeObserver(updateToggleState);
    };
  }, [alarmRow]);

  return {
    isAlarmActive,
    firstOperator,
    secondOperator,
    firstCondition,
    secondCondition,
    returnUnit,
    renderCheckBoxButton,
    handleClickOnNode,
    returnIcon,
    buildAncestorBreadCrumbs,
    handleFirstMatchOnClick,
    handleFirstConditionOnBlur,
    handleSecondMatchOnClick,
    handleSecondConditionOnBlur,
    toggleHandleClick,
    setFirstCondition,
    setSecondCondition,
  };
};
